import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Clock, Mail, MessageSquare, CheckCircle2, AlertCircle, Package, Eye, RotateCcw, Check, Trash2, RefreshCw, Search, Inbox, BarChart2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { KpiCard } from '../components/ui/KpiCard';
import { Modal } from '../components/ui/Modal';
import { Button } from '../components/ui/Button';

const API = import.meta.env.VITE_API_URL || '';

function api(path, opts = {}) {
  const hasBody = opts.body != null;
  const headers = hasBody ? { 'Content-Type': 'application/json' } : {};
  return fetch(`${API}${path}`, {
    ...opts,
    credentials: 'include',
    headers: { ...headers, ...opts.headers },
    body: hasBody ? JSON.stringify(opts.body) : undefined,
  }).then(async (r) => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || data.message || `HTTP ${r.status}`);
    return data;
  });
}

const STATUS_CONFIG = {
  offen:           { key: 'offen',           Icon: Clock,         variant: 'warning' },
  email_gesendet:  { key: 'email_gesendet',  Icon: Mail,          variant: 'info' },
  beantwortet:     { key: 'beantwortet',     Icon: MessageSquare, variant: 'default', cls: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400' },
  abgeschlossen:   { key: 'abgeschlossen',   Icon: CheckCircle2,  variant: 'success' },
  fehler:          { key: 'fehler',          Icon: AlertCircle,   variant: 'error' },
};

function StatusBadge({ status }) {
  const { t } = useLanguage();
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.offen;
  const { Icon, variant, cls } = cfg;

  const variantClasses = {
    warning: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    info:    'bg-primary-500/10 text-primary-600 dark:text-primary-400',
    success: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    error:   'bg-red-500/10 text-red-600 dark:text-red-400',
    default: '',
  };

  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-badge text-[12px] font-medium whitespace-nowrap ${cls || variantClasses[variant] || variantClasses.default}`}>
      <Icon className="w-3 h-3" />
      {t(`adressklaerung.${cfg.key}`)}
    </span>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' });
}

function timeAgo(dateStr, t) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return '—';
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return t('common.justNow') || 'just now';
  if (mins < 60) return `${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function ConfirmDialog({ open, onClose, onConfirm, message, loading }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-surface-elevated border border-line-hover rounded-card w-full max-w-[400px] shadow-modal animate-slide-in p-5">
        <p className="text-sm text-content mb-4">{message}</p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
            {loading ? '...' : 'Cancel'}
          </Button>
          <Button variant="primary" size="sm" onClick={onConfirm} loading={loading}>
            OK
          </Button>
        </div>
      </div>
    </div>
  );
}

function EmailPreviewOverlay({ html, onClose }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-card w-full max-w-[700px] h-[80vh] shadow-modal animate-slide-in flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-line">
          <span className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">E-Mail Preview</span>
          <button onClick={onClose} className="text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-secondary,#4B5563)] text-lg leading-none">&times;</button>
        </div>
        <div className="flex-1 overflow-hidden">
          <iframe
            srcDoc={html}
            title="Email Preview"
            className="w-full h-full border-0"
            sandbox="allow-same-origin"
          />
        </div>
      </div>
    </div>
  );
}

function DetailModal({ open, onClose, item, t, onResend, onCloseItem }) {
  const [emails, setEmails] = useState([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);

  useEffect(() => {
    if (!item?.id) { setEmails([]); return; }
    setEmailsLoading(true);
    api(`/api/adressklaerungen/${item.id}/emails`)
      .then(res => setEmails(res.emails || []))
      .catch(() => setEmails([]))
      .finally(() => setEmailsLoading(false));
  }, [item?.id]);

  if (!item) return null;

  const timeline = [
    { label: t('adressklaerung.created') || 'Created', time: item.created_at },
    { label: t('adressklaerung.email_gesendet'), time: item.email_gesendet_at },
    { label: t('adressklaerung.beantwortet'), time: item.antwort_empfangen_at },
    { label: t('adressklaerung.abgeschlossen'), time: item.status === 'abgeschlossen' ? item.updated_at : null },
  ];

  return (
    <Modal open={open} onClose={onClose} title={`${t('adressklaerung.title')} #${item.id} — ${t('adressklaerung.bestellung')} ${item.bestellung_nr || '—'}`} size="xl">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        {/* Left column (60%) */}
        <div className="md:col-span-3 space-y-4">
          <div className="bg-surface-card border border-line-card rounded-card p-4 space-y-3">
            <h3 className="text-xs font-bold text-content-secondary uppercase tracking-wider mb-2">
              {t('adressklaerung.extractedData') || 'Extracted Data'}
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-content-secondary text-xs">{t('adressklaerung.kunde') || 'Customer'}</span>
                <p className="text-content font-medium">{item.kunde_name || '—'}</p>
              </div>
              <div>
                <span className="text-content-secondary text-xs">E-Mail</span>
                <p className="text-content font-medium">{item.empfaenger_email || '—'}</p>
              </div>
              <div>
                <span className="text-content-secondary text-xs">PLZ / {t('adressklaerung.ort') || 'City'}</span>
                <p className="text-content font-medium">{[item.plz, item.ort].filter(Boolean).join(' ') || '—'}</p>
              </div>
              <div>
                <span className="text-content-secondary text-xs">OMS Case</span>
                <p className="text-content font-medium">{item.oms_case_name || `#${item.oms_case_id}`}</p>
              </div>
              {item.korrigierte_adresse && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <p style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)', marginBottom: 4 }}>
                    {t('adressklaerung.korrigierteAdresse')}
                  </p>
                  <p style={{ fontSize: 14, color: 'var(--text-primary)', fontWeight: 500, background: 'var(--primary-light)', padding: '8px 12px', borderRadius: 8, borderLeft: '3px solid var(--primary)' }}>
                    {item.korrigierte_adresse}
                  </p>
                </div>
              )}
            </div>
            <div>
              <span className="text-content-secondary text-xs">{t('adressklaerung.problem') || 'Problem'}</span>
              <p className="text-content text-sm mt-0.5">{item.problem_beschreibung || '—'}</p>
            </div>
          </div>

          {item.antwort_raw && (
            <div className="bg-surface-card border border-line-card rounded-card p-4">
              <h3 className="text-xs font-bold text-content-secondary uppercase tracking-wider mb-2">
                {t('adressklaerung.antwortRaw')}
              </h3>
              <p className="text-content text-sm whitespace-pre-wrap">{item.antwort_raw}</p>
            </div>
          )}


          {item.fehler_log && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-card p-4">
              <h3 className="text-xs font-bold text-red-600 dark:text-red-400 uppercase tracking-wider mb-2">
                {t('adressklaerung.fehler')}
              </h3>
              <p className="text-red-600 dark:text-red-400 text-sm">{item.fehler_log}</p>
            </div>
          )}

          {/* E-Mail Archive */}
          <div className="bg-surface-card border border-line-card rounded-card p-4">
            <h3 className="text-xs font-bold text-content-secondary uppercase tracking-wider mb-3">
              {t('adressklaerung.emailArchive')}
            </h3>
            {emailsLoading ? (
              <div className="flex items-center gap-2 text-content-secondary text-sm py-2">
                <div className="animate-spin h-3 w-3 border-2 border-primary-500 border-t-transparent rounded-full" />
                Loading...
              </div>
            ) : emails.length === 0 ? (
              <p className="text-content-secondary text-sm">{t('adressklaerung.emailNoEmails')}</p>
            ) : (
              <div className="space-y-2">
                {emails.map((em) => (
                  <div key={em.id} className="flex items-center justify-between gap-3 px-3 py-2 bg-surface-inset rounded-button">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                        em.email_type === 'initial'
                          ? 'bg-primary-500/10 text-primary-600 dark:text-primary-400'
                          : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                      }`}>
                        {em.email_type === 'initial' ? t('adressklaerung.emailInitial') : t('adressklaerung.emailFollowup')}
                      </span>
                      <span className="text-xs text-content-secondary truncate">{em.to_email}</span>
                      <span className="text-[11px] text-content-tertiary">{formatDate(em.sent_at)}</span>
                    </div>
                    <button
                      onClick={() => setPreviewHtml(em.html)}
                      className="shrink-0 px-2 py-1 text-[11px] font-medium text-primary-600 dark:text-primary-400 border border-primary-200 dark:border-primary-500/30 rounded-button hover:bg-primary-50 dark:hover:bg-primary-500/10 transition-colors"
                    >
                      {t('adressklaerung.emailPreview')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right column (40%) */}
        <div className="md:col-span-2 space-y-4">
          <div className="bg-surface-card border border-line-card rounded-card p-4">
            <h3 className="text-xs font-bold text-content-secondary uppercase tracking-wider mb-3">
              {t('adressklaerung.statusTimeline')}
            </h3>
            <div className="space-y-3">
              {timeline.map((step, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${step.time ? 'bg-primary-500' : 'bg-surface-inset border border-line'}`} />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-content">{step.label}</p>
                    <p className="text-[11px] text-content-secondary">{step.time ? formatDate(step.time) : '—'}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-surface-card border border-line-card rounded-card p-4">
            <h3 className="text-xs font-bold text-content-secondary uppercase tracking-wider mb-3">Status</h3>
            <StatusBadge status={item.status} />
          </div>

          <div className="space-y-2">
            {(item.status === 'offen' || item.status === 'fehler') && (
              <Button variant="secondary" size="sm" icon={RotateCcw} onClick={() => onResend(item)} className="w-full">
                {t('adressklaerung.resend')}
              </Button>
            )}
            {item.status !== 'abgeschlossen' && (
              <Button variant="secondary" size="sm" icon={Check} onClick={() => onCloseItem(item)} className="w-full">
                {t('adressklaerung.abgeschlossen')}
              </Button>
            )}
          </div>
        </div>
      </div>
      {previewHtml && <EmailPreviewOverlay html={previewHtml} onClose={() => setPreviewHtml(null)} />}
    </Modal>
  );
}

export default function AdressklaerungPage() {
  const { user } = useAuth();
  const { t } = useLanguage();

  const [data, setData] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [detailItem, setDetailItem] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const limit = 50;

  const fetchStats = useCallback(async () => {
    try {
      const s = await api('/api/adressklaerungen/stats');
      setStats(s);
    } catch { /* silent */ }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (statusFilter) params.set('status', statusFilter);
      if (search) params.set('search', search);
      const res = await api(`/api/adressklaerungen?${params}`);
      setData(res.data || []);
      setTotal(res.total || 0);
    } catch { /* silent */ }
    setLoading(false);
  }, [page, statusFilter, search]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const refresh = () => { fetchStats(); fetchData(); };

  const handleTrigger = async (endpoint, key) => {
    setTriggerLoading(key);
    try {
      await api(`/api/adressklaerungen/${endpoint}`, { method: 'POST' });
      refresh();
    } catch { /* silent */ }
    setTriggerLoading(null);
  };

  const handleResend = async (item) => {
    setConfirm({
      message: t('adressklaerung.resendConfirm'),
      onConfirm: async () => {
        setActionLoading(true);
        try {
          await api(`/api/adressklaerungen/${item.id}/resend`, { method: 'POST' });
          refresh();
          setConfirm(null);
          setDetailItem(null);
        } catch { /* silent */ }
        setActionLoading(false);
      },
    });
  };

  const handleClose = async (item) => {
    setConfirm({
      message: t('adressklaerung.closeConfirm'),
      onConfirm: async () => {
        setActionLoading(true);
        try {
          await api(`/api/adressklaerungen/${item.id}/close`, { method: 'POST' });
          refresh();
          setConfirm(null);
          setDetailItem(null);
        } catch { /* silent */ }
        setActionLoading(false);
      },
    });
  };

  const handleDelete = async (item) => {
    setConfirm({
      message: t('adressklaerung.deleteConfirm'),
      onConfirm: async () => {
        setActionLoading(true);
        try {
          await api(`/api/adressklaerungen/${item.id}`, { method: 'DELETE' });
          refresh();
          setConfirm(null);
        } catch { /* silent */ }
        setActionLoading(false);
      },
    });
  };

  const handleBulkDelete = () => {
    setConfirm({
      message: `${selectedIds.length} ${t('adressklaerung.deleteConfirm')}`,
      onConfirm: async () => {
        setActionLoading(true);
        try {
          await api('/api/adressklaerungen/bulk', { method: 'DELETE', body: { ids: selectedIds } });
          setSelectedIds([]);
          refresh();
          setConfirm(null);
        } catch { /* silent */ }
        setActionLoading(false);
      },
    });
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === data.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(data.map(r => r.id));
    }
  };

  const totalPages = Math.ceil(total / limit);

  const kpiCards = [
    { label: t('adressklaerung.offen'),           value: stats.offen || 0,           Icon: Clock,          color: 'bg-[#EBF4FD] text-[#338edc]' },
    { label: t('adressklaerung.email_gesendet'),   value: stats.email_gesendet || 0,  Icon: Mail,           color: 'bg-[#ECFEFF] text-[#22D3EE]' },
    { label: t('adressklaerung.beantwortet'),      value: stats.beantwortet || 0,     Icon: MessageSquare,  color: 'bg-[#ECFDF5] text-[#10B981]' },
    { label: t('adressklaerung.abgeschlossen'),    value: stats.abgeschlossen || 0,   Icon: CheckCircle2,   color: 'bg-[#FFFBEB] text-[#F59E0B]' },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-button bg-primary-500/10 text-primary-600 dark:text-primary-400">
            <MapPin className="w-5 h-5" />
          </div>
          <h1 className="text-xl font-bold text-content">{t('adressklaerung.title')}</h1>
        </div>
        <Link to="/kunden/adressklaerungen/stats">
          <button className="flex items-center gap-2 px-3 py-2 text-sm border border-[var(--border-card)] rounded-button hover:bg-[var(--bg-card-hover)] transition-colors text-content-secondary">
            <BarChart2 className="w-4 h-4" />
            {t('adressklaerung.stats')}
          </button>
        </Link>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {kpiCards.map((kpi, i) => (
          <KpiCard key={i} label={kpi.label} value={kpi.value} icon={kpi.Icon} color={kpi.color} />
        ))}
      </div>

      {/* Filter + Search Row */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text"
          placeholder={`${t('adressklaerung.bestellung')} / ${t('adressklaerung.kunde') || 'Customer'} / E-Mail`}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="px-3 py-2 text-sm bg-surface-inset border border-line rounded-button text-content placeholder:text-content-tertiary w-64 focus:outline-none focus:border-primary-500"
        />
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 text-sm bg-surface-inset border border-line rounded-button text-content focus:outline-none focus:border-primary-500"
        >
          <option value="">Status</option>
          {Object.keys(STATUS_CONFIG).map(s => (
            <option key={s} value={s}>{t(`adressklaerung.${s}`)}</option>
          ))}
        </select>
        <Button variant="secondary" size="sm" icon={RefreshCw} onClick={refresh}>
          Refresh
        </Button>
        {['administrator', 'admin'].includes(user?.rolle) && (
          <>
            <Button variant="ghost" size="sm" icon={Search} onClick={() => handleTrigger('run-cronjob', 'oms')} loading={triggerLoading === 'oms'}>
              {t('adressklaerung.checkOms')}
            </Button>
            <Button variant="ghost" size="sm" icon={Inbox} onClick={() => handleTrigger('check-incoming', 'imap')} loading={triggerLoading === 'imap'}>
              {t('adressklaerung.checkEmails')}
            </Button>
          </>
        )}
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3 px-4 py-2 bg-primary-50 dark:bg-primary-500/10 border border-primary-200 dark:border-primary-500/20 rounded-button mb-3">
          <span className="text-sm text-primary-700 dark:text-primary-300 font-medium">
            {selectedIds.length} {t('adressklaerung.selected') || 'selected'}
          </span>
          <button
            onClick={handleBulkDelete}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 border border-red-200 dark:border-red-500/30 rounded-button hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            {t('common.delete')}
          </button>
          <button
            onClick={() => setSelectedIds([])}
            className="text-sm text-content-secondary hover:text-content transition-colors"
          >
            {t('common.cancel')}
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-surface-card border border-line-card rounded-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line bg-surface-inset">
                <th className="w-10 px-3 py-3">
                  <input type="checkbox" checked={data.length > 0 && selectedIds.length === data.length} onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-[var(--border-default,#E5E7EB)] text-primary-600 focus:ring-primary-500 accent-primary-600" />
                </th>
                <th className="text-left px-4 py-3 text-[11px] font-bold text-content-secondary uppercase tracking-wider">{t('adressklaerung.bestellung')}</th>
                <th className="text-left px-4 py-3 text-[11px] font-bold text-content-secondary uppercase tracking-wider">{t('adressklaerung.colEmpfaenger')}</th>
                <th className="text-left px-4 py-3 text-[11px] font-bold text-content-secondary uppercase tracking-wider">{t('adressklaerung.colHaendler')}</th>
                <th className="text-left px-4 py-3 text-[11px] font-bold text-content-secondary uppercase tracking-wider">E-Mail</th>
                <th className="text-left px-4 py-3 text-[11px] font-bold text-content-secondary uppercase tracking-wider">{t('adressklaerung.problem') || 'Problem'}</th>
                <th className="text-left px-4 py-3 text-[11px] font-bold text-content-secondary uppercase tracking-wider">Status</th>
                <th className="text-center px-4 py-3 text-[11px] font-bold text-content-secondary uppercase tracking-wider">{t('adressklaerung.emails')}</th>
                <th className="text-left px-4 py-3 text-[11px] font-bold text-content-secondary uppercase tracking-wider">{t('adressklaerung.sent') || 'Sent'}</th>
                <th className="text-left px-4 py-3 text-[11px] font-bold text-content-secondary uppercase tracking-wider">{t('adressklaerung.updated') || 'Updated'}</th>
                <th className="text-right px-4 py-3 text-[11px] font-bold text-content-secondary uppercase tracking-wider">{t('adressklaerung.actions') || 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} className="text-center py-12 text-content-secondary">
                  <div className="inline-flex items-center gap-2">
                    <div className="animate-spin h-4 w-4 border-2 border-primary-500 border-t-transparent rounded-full" />
                    Loading...
                  </div>
                </td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={11} className="text-center py-12 text-content-secondary">{t('adressklaerung.noData')}</td></tr>
              ) : data.map((row) => (
                <tr key={row.id} className={`border-b border-line last:border-b-0 hover:bg-surface-hover transition-colors ${selectedIds.includes(row.id) ? 'bg-[var(--bg-selected-row)]' : ''}`}>
                  <td className="w-10 px-3 py-3">
                    <input type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => toggleSelect(row.id)}
                      className="w-4 h-4 rounded border-[var(--border-default,#E5E7EB)] text-primary-600 focus:ring-primary-500 accent-primary-600" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Package className="w-3.5 h-3.5 text-content-secondary shrink-0" />
                      <span className="font-medium text-content">{row.bestellung_nr || '—'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-content">{row.kunde_name || '—'}</td>
                  <td className="px-4 py-3 text-content-secondary text-xs">{row.haendler_name || '—'}</td>
                  <td className="px-4 py-3 text-content-secondary text-xs">{row.empfaenger_email || '—'}</td>
                  <td className="px-4 py-3 text-content-secondary text-xs max-w-[200px] truncate" title={row.problem_beschreibung}>
                    {row.problem_beschreibung ? (row.problem_beschreibung.length > 60 ? row.problem_beschreibung.slice(0, 60) + '...' : row.problem_beschreibung) : '—'}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={row.status} /></td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold ${
                      row.email_count > 0
                        ? 'bg-primary-500/10 text-primary-600 dark:text-primary-400'
                        : 'bg-gray-100 text-[var(--text-tertiary,#6B7280)] dark:bg-gray-800 dark:text-[var(--text-secondary,#4B5563)]'
                    }`}>
                      {row.email_count || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-content-secondary text-xs">{formatDate(row.email_gesendet_at)}</td>
                  <td className="px-4 py-3 text-content-secondary text-xs">{timeAgo(row.updated_at, t)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setDetailItem(row)}
                        className="p-1.5 rounded-button text-content-secondary hover:text-content hover:bg-surface-hover transition-colors"
                        title="Detail"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                      {(row.status === 'offen' || row.status === 'fehler') && (
                        <button
                          onClick={() => handleResend(row)}
                          className="p-1.5 rounded-button text-content-secondary hover:text-primary-600 hover:bg-primary-500/10 transition-colors"
                          title={t('adressklaerung.resend')}
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {row.status !== 'abgeschlossen' && (
                        <button
                          onClick={() => handleClose(row)}
                          className="p-1.5 rounded-button text-content-secondary hover:text-emerald-600 hover:bg-emerald-500/10 transition-colors"
                          title={t('adressklaerung.abgeschlossen')}
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {['administrator', 'admin'].includes(user?.rolle) && (
                        <button
                          onClick={() => handleDelete(row)}
                          className="p-1.5 rounded-button text-content-secondary hover:text-red-600 hover:bg-red-500/10 transition-colors"
                          title={t('adressklaerung.deleteConfirm')}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-line">
            <span className="text-xs text-content-secondary">
              {total} {t('adressklaerung.total') || 'total'}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-2 py-1 text-xs rounded-button text-content-secondary hover:bg-surface-hover disabled:opacity-40 transition-colors"
              >
                &laquo;
              </button>
              <span className="px-2 py-1 text-xs text-content">{page} / {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-2 py-1 text-xs rounded-button text-content-secondary hover:bg-surface-hover disabled:opacity-40 transition-colors"
              >
                &raquo;
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <DetailModal
        open={!!detailItem}
        onClose={() => setDetailItem(null)}
        item={detailItem}
        t={t}
        onResend={handleResend}
        onCloseItem={handleClose}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={confirm?.onConfirm}
        message={confirm?.message}
        loading={actionLoading}
      />
    </div>
  );
}
